/**
 * Captura da tela com o ponto reclamado destacado.
 *
 * O html2canvas-pro pesa perto de 50 kB comprimido, mais que todo o resto do
 * widget somado. Por isso ele entra por importação dinâmica: quem nunca abre o
 * painel de feedback jamais paga esse custo.
 *
 * A ideia do recorte e do marcador vem do Sensa; a máscara de dados sensíveis
 * é adição daqui, porque no Clixy a tela pertence a um cliente e pode conter
 * dado de terceiro.
 */

const JPEG_QUALITY = 0.7;
const CROP_PADDING = 180;
const MAX_OUTPUT_DIMENSION = 1200;

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Elementos que nunca podem sair na foto.
 *
 * Campo de senha é óbvio. `data-clixy-mask` é o escape que o cliente usa para
 * proteger o que só ele sabe que é sensível, como dado de paciente ou saldo.
 */
const MASK_SELECTOR = '[data-clixy-mask], input[type="password"]';

function applyMasks(): () => void {
  const masked = Array.from(document.querySelectorAll<HTMLElement>(MASK_SELECTOR));
  const previous = masked.map((element) => element.style.filter);

  masked.forEach((element) => {
    element.style.filter = 'blur(12px)';
  });

  return () => {
    masked.forEach((element, index) => {
      element.style.filter = previous[index] ?? '';
    });
  };
}

function drawMarker(canvas: HTMLCanvasElement, marker: Rect, accentColor: string): void {
  const context = canvas.getContext('2d');
  if (!context) return;

  context.fillStyle = `${accentColor}2e`;
  context.fillRect(marker.x, marker.y, marker.w, marker.h);

  context.strokeStyle = accentColor;
  context.lineWidth = 3;
  context.strokeRect(marker.x, marker.y, marker.w, marker.h);

  context.beginPath();
  context.arc(marker.x, marker.y, 9, 0, Math.PI * 2);
  context.fillStyle = accentColor;
  context.fill();
  context.strokeStyle = '#ffffff';
  context.lineWidth = 2.5;
  context.stroke();
}

function cropAround(source: HTMLCanvasElement, marker: Rect): HTMLCanvasElement {
  const cropX = Math.max(0, Math.floor(marker.x - CROP_PADDING));
  const cropY = Math.max(0, Math.floor(marker.y - CROP_PADDING));
  const cropWidth = Math.min(source.width - cropX, Math.ceil(marker.w + CROP_PADDING * 2));
  const cropHeight = Math.min(source.height - cropY, Math.ceil(marker.h + CROP_PADDING * 2));

  const scale = Math.min(1, MAX_OUTPUT_DIMENSION / Math.max(cropWidth, cropHeight));
  const outputWidth = Math.max(1, Math.round(cropWidth * scale));
  const outputHeight = Math.max(1, Math.round(cropHeight * scale));

  const target = document.createElement('canvas');
  target.width = outputWidth;
  target.height = outputHeight;

  const context = target.getContext('2d');
  if (!context) return source;

  context.drawImage(
    source,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    outputWidth,
    outputHeight,
  );
  return target;
}

interface CaptureOptions {
  marker?: Rect | null;
  accentColor: string;
  /** Origem de onde o `w.js` foi servido, que é onde o chunk também mora. */
  assetOrigin: string;
}

const CHUNK_PATH = '/clixy-screenshot.js';

let chunkPromise: Promise<void> | null = null;

/**
 * Carrega o bundle de captura sob demanda, uma vez por página.
 *
 * A biblioteca chega por tag script em vez de `import()` porque o widget é
 * distribuído em IIFE: um import dinâmico seria embutido no bundle principal
 * pelo empacotador, anulando justamente a economia que se busca aqui.
 */
function loadCaptureLibrary(assetOrigin: string): Promise<void> {
  if (window.__clixyHtml2Canvas) return Promise.resolve();
  if (chunkPromise) return chunkPromise;

  chunkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${assetOrigin}${CHUNK_PATH}`;
    script.async = true;
    script.setAttribute('data-clixy-ignore', 'true');
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      chunkPromise = null;
      reject(new Error('falha ao carregar o módulo de captura'));
    });
    document.head.appendChild(script);
  });

  return chunkPromise;
}

/** Captura o viewport. Devolve data URI JPEG, ou null se a captura falhar. */
export async function captureScreenshot({
  marker,
  accentColor,
  assetOrigin,
}: CaptureOptions): Promise<string | null> {
  const restoreMasks = applyMasks();

  try {
    await loadCaptureLibrary(assetOrigin);

    const html2canvas = window.__clixyHtml2Canvas;
    if (!html2canvas) throw new Error('módulo de captura indisponível');

    const canvas = await html2canvas(document.body, {
      width: window.innerWidth,
      height: window.innerHeight,
      x: window.scrollX,
      y: window.scrollY,
      scale: 1,
      logging: false,
      useCORS: true,
      // O painel do widget e os adornos do picker não entram na foto: a pessoa
      // quer registrar a tela do produto, não a nossa interface por cima dela.
      ignoreElements: (element) =>
        element.id === 'clixy-widget-root' || element.hasAttribute('data-clixy-ignore'),
    });

    if (!marker) return canvas.toDataURL('image/jpeg', JPEG_QUALITY);

    drawMarker(canvas, marker, accentColor);
    return cropAround(canvas, marker).toDataURL('image/jpeg', JPEG_QUALITY);
  } catch (error) {
    // Captura é acessório: o feedback continua valendo sem ela, e derrubar o
    // envio por causa de uma imagem seria pior para quem está reportando.
    console.warn('[clixy] não foi possível capturar a tela', error);
    return null;
  } finally {
    restoreMasks();
  }
}
